/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/LineItemRow.tsx
 * PURPOSE: Single line-item row inside the scope sheet grid.
 */

import { useMemo } from 'react'
import { cn, formatCurrency } from '@/lib/utils'
import type { QuoteSection, QuoteLineItem } from '@/lib/quoteApi'
import type { CellErrorState } from '@/hooks/useBuilderEditsBuffer'
import { computeLine } from '@/lib/quote-builder-math'
import {
  evaluateCell,
  isFormulaText,
  type FormulaContext,
} from '@/lib/quote-builder-math/formula'
import EditableCell from './EditableCell'
import type { Column, CustomColumnDef } from './ScopeSheetGridTypes'
import { BASE_COLUMN_BANDS } from './scope-sheet-columns'

// Number of fixed columns rendered before the dynamic "Custom" band starts.
// Counts every column in BASE_COLUMN_BANDS regardless of preset/hidden state
// (hidden columns still occupy a grid track of width 0). Derived from the
// canonical band definition rather than hand-coded so adding optional
// columns to scope-sheet-columns.ts does not silently shift the cursor map.
const FIXED_COLUMN_COUNT: number = BASE_COLUMN_BANDS.reduce(
  (n, band) => n + band.columns.length,
  0,
)

// Number of columns rendered with hand-written cells (cols 0..16 — the
// 9 baseline bands). Everything from BASELINE_COLUMN_COUNT to
// FIXED_COLUMN_COUNT-1 is the "Advanced" band, rendered via the
// `advancedColumns` prop instead of inline JSX.
const BASELINE_COLUMN_COUNT = 17

interface LineItemRowProps {
  section: QuoteSection
  item: QuoteLineItem
  index: number
  flatRowIndex: number
  onUpdate: (patch: Partial<QuoteLineItem>) => void
  onDelete: () => void
  onSelect: (colIndex?: number, opts?: { startEditing?: boolean }) => void
  /**
   * Right-click handler — fires the floating row context menu (insert above /
   * below, duplicate, clear, delete). Wired by ScopeSheetGrid; LineItemRow
   * just forwards the event so the parent can read clientX/clientY.
   */
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void
  isSelected: boolean
  isEditable: boolean
  activeCellCol?: number
  isEditing: boolean
  editValue: string
  onStartEditing: () => void
  onSetEditValue: (value: string) => void
  onCommitEdit: (value: string, field: string) => void
  /**
   * Cell-level keyboard handler — passed straight through to every
   * EditableCell input so Enter/Tab/Arrow navigation is deterministic
   * even when an input has focus.
   */
  onCellKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  getOptimisticValue?: (itemId: string, field: string) => unknown | undefined
  getCellErrorState?: (itemId: string, field: string) => CellErrorState
  gridStyle: React.CSSProperties
  /**
   * User-defined columns appended after the fixed grid. Per-line values live
   * in `item.custom_values[key]` and cascade to `customColumns[i].default_value`.
   */
  customColumns?: CustomColumnDef[]
  /**
   * The Advanced band's resolved columns (after preset + visibility).
   * Rendered between the 17 baseline cells and the custom band. Hidden
   * entries occupy a width-0 grid track and render an empty placeholder
   * so positional indices stay aligned with `BASE_COLUMN_BANDS`.
   */
  advancedColumns?: Column[]
}

// Editable column map: col-index → { field, type }
const EDITABLE_COLS: Record<number, { field: string; type: 'text' | 'number' | 'currency' }> = {
  1: { field: 'location', type: 'text' },
  2: { field: 'description', type: 'text' },
  3: { field: 'application', type: 'text' },
  4: { field: 'quantity', type: 'number' },
  5: { field: 'unit', type: 'text' },
  6: { field: 'productivity_pmpd', type: 'number' },
  9: { field: 'labour_chargeout', type: 'currency' },
  11: { field: 'adjusted_rate', type: 'currency' },
  12: { field: 'material_chargeout', type: 'currency' },
  13: { field: 'plant_chargeout', type: 'currency' },
  14: { field: 'subcon_chargeout', type: 'currency' },
}

export default function LineItemRow({
  section,
  item,
  index,
  flatRowIndex,
  onUpdate,
  onDelete,
  onSelect,
  onContextMenu,
  isSelected,
  isEditable,
  activeCellCol,
  isEditing,
  editValue,
  onStartEditing,
  onSetEditValue,
  onCommitEdit,
  onCellKeyDown,
  getOptimisticValue,
  getCellErrorState,
  gridStyle,
  customColumns,
  advancedColumns,
}: LineItemRowProps) {
  const toNumber = (v: unknown): number => {
    if (typeof v === 'number') return v
    const n = Number.parseFloat(String(v ?? 0))
    return Number.isFinite(n) ? n : 0
  }

  const getOptVal = (field: string) => getOptimisticValue?.(item.id, field)
  const getErrState = (field: string) => getCellErrorState?.(item.id, field) || 'none'

  const resolvePricingMode = (): 'manual' | 'calculated' => {
    if (item.pricing_mode === 'manual' || item.pricing_mode === 'calculated') return item.pricing_mode
    return item.override_chargeout != null ? 'manual' : 'calculated'
  }
  const isManual = resolvePricingMode() === 'manual'

  // Live frontend compute — drives Hrs / Days / Calc Rate cells immediately
  // as the user types, instead of waiting for the debounced backend recalc.
  // Backend remains authoritative on save; this is purely the visible-cell
  // optimism layer. See QUOTE-BUILDER-MATH-COMPUTE-001.
  const liveInputs = {
    quantity: getOptVal('quantity') ?? item.quantity,
    productivity_pmpd: getOptVal('productivity_pmpd') ?? item.productivity_pmpd,
    labour_chargeout: getOptVal('labour_chargeout') ?? item.labour_chargeout,
    labour_cost: getOptVal('labour_cost') ?? item.labour_cost,
    material_chargeout: getOptVal('material_chargeout') ?? item.material_chargeout,
    material_cost: getOptVal('material_cost') ?? item.material_cost,
    plant_chargeout: getOptVal('plant_chargeout') ?? item.plant_chargeout,
    plant_cost: getOptVal('plant_cost') ?? item.plant_cost,
    subcon_chargeout: getOptVal('subcon_chargeout') ?? item.subcon_chargeout,
    subcon_cost: getOptVal('subcon_cost') ?? item.subcon_cost,
    adjusted_rate: getOptVal('adjusted_rate') ?? item.adjusted_rate,
  }
  const live = computeLine(liveInputs)

  const labourChargeout = parseFloat(String(liveInputs.labour_chargeout || 0))
  const materialChargeout = parseFloat(String(liveInputs.material_chargeout || 0))
  const plantChargeout = parseFloat(String(liveInputs.plant_chargeout || 0))
  const subconChargeout = parseFloat(String(liveInputs.subcon_chargeout || 0))
  const subtotal = live.chargeout_total || (labourChargeout + materialChargeout + plantChargeout + subconChargeout)

  const hasAdjustedRate = item.adjusted_rate != null && parseFloat(String(item.adjusted_rate)) > 0
  // Show the live value when the user has driving inputs set; only fall back
  // to the backend's stored value if the frontend can't compute (no qty/pmpd).
  const displayLabourHours = live.labour_hours > 0
    ? live.labour_hours
    : parseFloat(String(item.labour_hours || 0))
  const displayLabourDays = live.labour_days !== 0
    ? live.labour_days
    : parseFloat(String(item.labour_days || 0))
  const displayRatePerUnit = live.rate_per_unit > 0
    ? live.rate_per_unit
    : parseFloat(String(item.rate_per_unit || 0))

  const formatValue = (value: unknown, type: 'currency' | 'number' | 'text' = 'text') => {
    if (value == null || value === '') return '-'
    if (type === 'currency') return formatCurrency(parseFloat(String(value)) || 0)
    if (type === 'number') return parseFloat(String(value)).toFixed(1)
    return String(value)
  }

  // Margin %: prefer the live value when chargeouts AND costs are both set
  // (only meaningful when both sides exist). Otherwise fall back to backend.
  const hasCostInputs =
    parseFloat(String(item.labour_cost ?? 0)) > 0 ||
    parseFloat(String(item.material_cost ?? 0)) > 0 ||
    parseFloat(String(item.plant_cost ?? 0)) > 0 ||
    parseFloat(String(item.subcon_cost ?? 0)) > 0
  const marginVal = hasCostInputs && live.chargeout_total > 0
    ? live.margin_percent
    : (parseFloat(String(item.margin_percent)) || 0)
  const marginClass = marginVal >= 30
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400'
    : marginVal >= 20
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400'
    : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'

  const isCellActive = (colIndex: number) => activeCellCol === colIndex

  // Variable resolution context for formula eval. Includes both raw line-item
  // fields AND aliases that match the names estimators actually type
  // (qty, pmpd, rate, hrs, days). All numeric, normalized through toNumber.
  // Uses live optimistic values so =qty*2 reflects the in-flight typed qty.
  const formulaCtx: FormulaContext = useMemo(() => {
    const N = (v: unknown): number => toNumber(v)
    const qty = N(getOptVal('quantity') ?? item.quantity)
    const pmpd = N(getOptVal('productivity_pmpd') ?? item.productivity_pmpd)
    const labour_chargeout = N(getOptVal('labour_chargeout') ?? item.labour_chargeout)
    const material_chargeout = N(getOptVal('material_chargeout') ?? item.material_chargeout)
    const plant_chargeout = N(getOptVal('plant_chargeout') ?? item.plant_chargeout)
    const subcon_chargeout = N(getOptVal('subcon_chargeout') ?? item.subcon_chargeout)
    const adjusted_rate = N(getOptVal('adjusted_rate') ?? item.adjusted_rate)
    return {
      // canonical line-item fields
      quantity: qty,
      productivity_pmpd: pmpd,
      labour_chargeout,
      material_chargeout,
      plant_chargeout,
      subcon_chargeout,
      adjusted_rate,
      labour_days: pmpd > 0 ? qty / pmpd : 0,
      labour_hours: pmpd > 0 ? (qty / pmpd) * 8 : 0,
      rate_per_unit: qty > 0 ? labour_chargeout / qty : 0,
      chargeout_total: labour_chargeout + material_chargeout + plant_chargeout + subcon_chargeout,
      // estimator-friendly aliases (Excel users type these)
      qty,
      pmpd,
      rate: adjusted_rate || (qty > 0 ? labour_chargeout / qty : 0),
      hrs: pmpd > 0 ? (qty / pmpd) * 8 : 0,
      days: pmpd > 0 ? qty / pmpd : 0,
      labour: labour_chargeout,
      material: material_chargeout,
      plant: plant_chargeout,
      subcon: subcon_chargeout,
    }
  }, [item, getOptVal])

  const handleCommit = (value: string, colIndex: number) => {
    const colDef = EDITABLE_COLS[colIndex]
    if (!colDef) return
    const patch: Record<string, unknown> = {}

    // Formula path — caller typed "=expression". Persist the expression text in
    // formulas[field] AND cache the evaluated numeric in the typed column so
    // (a) old API clients that ignore `formulas` still see a valid number,
    // (b) recalculation cascades work without backend formula awareness.
    if (isFormulaText(value)) {
      const result = evaluateCell(value, formulaCtx)
      const existing = (item.formulas ?? {}) as Record<string, string>
      const nextFormulas = { ...existing, [colDef.field]: value }
      patch.formulas = nextFormulas
      if (colDef.type === 'number' || colDef.type === 'currency') {
        patch[colDef.field] = result.ok ? result.value : null
      } else {
        patch[colDef.field] = result.ok ? String(result.value) : value
      }
      onUpdate(patch)
      return
    }

    // Plain value path — clear any existing formula on this field, then save.
    const existing = (item.formulas ?? {}) as Record<string, string>
    if (colDef.field in existing) {
      const next = { ...existing }
      delete next[colDef.field]
      patch.formulas = next
    }
    if (colDef.type === 'number' || colDef.type === 'currency') {
      patch[colDef.field] = value ? parseFloat(value) : null
    } else {
      patch[colDef.field] = value
    }
    onUpdate(patch)
  }

  // Per-cell persisted formula text (e.g. "=qty * 0.5"). Tolerates both
  // legitimate { formulas: {...} } payloads and missing/null shapes.
  const formulasMap = (item.formulas ?? {}) as Record<string, string>

  // Helper to build an EditableCell for a given column index
  const cell = (colIdx: number, field: string, val: unknown, extra: Partial<React.ComponentProps<typeof EditableCell>> = {}) => {
    const cellFormula = formulasMap[field] ?? null
    return (
      <EditableCell
        field={field}
        value={val}
        optimisticValue={getOptVal(field)}
        errorState={getErrState(field)}
        isActive={isCellActive(colIdx)}
        isEditing={isEditing && isCellActive(colIdx)}
        editValue={isCellActive(colIdx) ? editValue : String(val || '')}
        onStartEdit={() => onSelect(colIdx, { startEditing: true })}
        onSetEditValue={onSetEditValue}
        onCommit={(v) => handleCommit(v, colIdx)}
        onKeyDown={onCellKeyDown}
        editable={isEditable}
        formula={cellFormula}
        formulaContext={formulaCtx}
        {...extra}
      />
    )
  }

  return (
    <div
      onClick={() => onSelect()}
      onContextMenu={onContextMenu}
      className={cn(
        'grid items-center h-full border-b border-slate-100 dark:border-slate-800 text-[11px]',
        'hover:bg-blue-50/50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors',
        isSelected && 'bg-blue-50 dark:bg-blue-900/30',
        index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50/50 dark:bg-slate-900/30'
      )}
      style={gridStyle}
    >
      {/* Col 0: Item number */}
      <div className="px-1.5 text-center text-slate-400 font-mono text-[10px]">
        <div className="flex items-center justify-center gap-1">
          <span>{item.item_number || index + 1}</span>
          {isEditable && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (isManual) {
                  onUpdate({ override_chargeout: null })
                  return
                }
                onUpdate({
                  labour_chargeout: toNumber(getOptVal('labour_chargeout') ?? item.labour_chargeout),
                  material_chargeout: toNumber(getOptVal('material_chargeout') ?? item.material_chargeout),
                  plant_chargeout: toNumber(getOptVal('plant_chargeout') ?? item.plant_chargeout),
                  subcon_chargeout: toNumber(getOptVal('subcon_chargeout') ?? item.subcon_chargeout),
                })
              }}
              className={cn(
                'px-1 py-0.5 rounded text-[9px] font-semibold border transition-colors',
                isManual
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
              )}
              title={isManual ? 'Manual pricing (click to switch to Calculated)' : 'Calculated pricing (click to switch to Manual)'}
            >
              {isManual ? 'M' : 'C'}
            </button>
          )}
        </div>
      </div>

      {cell(1, 'location', item.location, { className: 'text-slate-600 dark:text-slate-400' })}
      {cell(2, 'description', item.description, { className: 'font-medium text-slate-900 dark:text-white' })}
      {cell(3, 'application', item.product_system, { className: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/30 dark:bg-indigo-950/20' })}
      {cell(4, 'quantity', item.quantity, { type: 'number', align: 'right', className: 'bg-cyan-50/30 dark:bg-cyan-950/20' })}
      {cell(5, 'unit', item.unit || 'm²', { align: 'center', className: 'text-slate-500 bg-cyan-50/30 dark:bg-cyan-950/20 text-[10px]' })}
      {cell(6, 'productivity_pmpd', item.productivity_pmpd, { type: 'number', align: 'right', className: 'text-green-600 dark:text-green-400 bg-blue-50/30 dark:bg-blue-950/20' })}

      {/* Col 7–8: computed (live from qty/pmpd, see QUOTE-BUILDER-MATH-COMPUTE-001) */}
      <div
        className="px-1.5 text-right tabular-nums text-slate-400 bg-blue-50/30 dark:bg-blue-950/20"
        title={`Hours = qty / pmpd × 8 hr/day. Live: ${live.labour_hours.toFixed(2)} hr`}
      >
        {displayLabourHours > 0 ? formatValue(displayLabourHours, 'number') : '-'}
      </div>
      <div
        className="px-1.5 text-right tabular-nums text-slate-500 bg-blue-50/30 dark:bg-blue-950/20"
        title={`Days = qty / pmpd. Live: ${live.labour_days.toFixed(3)} d`}
      >
        {displayLabourDays !== 0 ? formatValue(displayLabourDays, 'number') : '-'}
      </div>

      {cell(9, 'labour_chargeout', item.labour_chargeout, { type: 'currency', align: 'right', className: 'text-blue-600 dark:text-blue-400 bg-blue-50/30 dark:bg-blue-950/20 font-medium' })}

      {/* Col 10: computed rate per unit (labour_chargeout / qty) */}
      <div
        className="px-1.5 text-right tabular-nums text-slate-400 bg-yellow-50/30 dark:bg-yellow-950/20"
        title={`Calculated rate = labour_chargeout / qty. Live: ${live.rate_per_unit.toFixed(2)} $/unit`}
      >
        {displayRatePerUnit > 0 ? formatValue(displayRatePerUnit, 'currency') : '-'}
      </div>

      {cell(11, 'adjusted_rate', item.adjusted_rate, {
        type: 'currency', align: 'right',
        className: cn('bg-yellow-50/30 dark:bg-yellow-950/20', hasAdjustedRate ? 'text-yellow-700 dark:text-yellow-300 font-semibold bg-yellow-100 dark:bg-yellow-900/40' : 'text-slate-300 dark:text-slate-600'),
      })}
      {cell(12, 'material_chargeout', item.material_chargeout, { type: 'currency', align: 'right', className: 'text-amber-600 dark:text-amber-400 bg-amber-50/30 dark:bg-amber-950/20 font-medium' })}
      {cell(13, 'plant_chargeout', item.plant_chargeout, { type: 'currency', align: 'right', className: 'text-purple-600 dark:text-purple-400 bg-purple-50/30 dark:bg-purple-950/20' })}
      {cell(14, 'subcon_chargeout', item.subcon_chargeout, { type: 'currency', align: 'right', className: 'text-teal-600 dark:text-teal-400 bg-teal-50/30 dark:bg-teal-950/20' })}

      {/* Col 15: margin */}
      <div className="px-1.5 flex items-center justify-center bg-emerald-50/30 dark:bg-emerald-950/20">
        <span className={cn('px-1 py-0.5 rounded text-[10px] font-medium', marginClass)}>{marginVal.toFixed(0)}%</span>
      </div>

      {/* Col 16: total */}
      <div className="px-1.5 text-right tabular-nums font-semibold text-slate-900 dark:text-white bg-emerald-50/30 dark:bg-emerald-950/20">
        {formatCurrency(subtotal)}
      </div>

      {/* Advanced columns (cols 17..33): SAM-parity opt-in fields. Each column
          renders even when hidden — its grid track collapses to width 0 — so
          positional cursor indexing stays aligned with BASE_COLUMN_BANDS. */}
      {(advancedColumns ?? []).map((col, i) => {
        const colIdx = BASELINE_COLUMN_COUNT + i
        // Hidden columns: render an empty placeholder. The grid track is
        // already 0px wide, so this never paints, but it keeps grid children
        // aligned 1:1 with `gridTemplateColumns` tracks.
        if (col.hidden) {
          return <div key={col.id} aria-hidden="true" />
        }
        const cellType: 'text' | 'number' | 'currency' = col.type ?? 'text'
        // Computed advanced columns — pull from live compute when possible,
        // fall back to the persisted backend value.
        if (col.computed) {
          let computed: number = 0
          if (col.id === 'total_cost') computed = live.total_cost
          else if (col.id === 'chargeout_total') computed = live.chargeout_total
          else if (col.id === 'margin') computed = live.margin
          else computed = toNumber((item as unknown as Record<string, unknown>)[col.id])
          const align = col.align === 'right' || col.align === 'left' ? col.align : 'right'
          return (
            <div
              key={col.id}
              className={cn(
                'px-1.5 tabular-nums bg-rose-50/30 dark:bg-rose-950/20 text-rose-700 dark:text-rose-300 font-medium',
                align === 'right' && 'text-right',
                align === 'left' && 'text-left',
              )}
              title={`${col.label} (computed). Live: ${computed.toFixed(2)}`}
            >
              {computed !== 0 ? formatValue(computed, cellType === 'text' ? 'text' : cellType) : '-'}
            </div>
          )
        }
        // Editable advanced cell — read-write through the line-item field
        // matching `col.id`. is_client_responsibility is a boolean field but
        // we surface it as text "Yes"/"No" for now (true UI control comes
        // with a future inspector toggle).
        const rawVal = (item as unknown as Record<string, unknown>)[col.id]
        const display: unknown =
          col.id === 'is_client_responsibility'
            ? rawVal === true
              ? 'Yes'
              : rawVal === false
              ? 'No'
              : ''
            : rawVal
        const editValueForCell = isCellActive(colIdx)
          ? editValue
          : display === undefined || display === null
          ? ''
          : String(display)
        const onCommitAdvanced = (v: string) => {
          let coerced: unknown = v
          if (col.id === 'is_client_responsibility') {
            const lower = v.trim().toLowerCase()
            if (lower === 'yes' || lower === 'y' || lower === 'true' || lower === '1') {
              coerced = true
            } else if (lower === 'no' || lower === 'n' || lower === 'false' || lower === '0' || lower === '') {
              coerced = false
            }
          } else if (cellType === 'number' || cellType === 'currency') {
            coerced = v === '' ? null : Number.parseFloat(v)
            if (typeof coerced === 'number' && Number.isNaN(coerced)) coerced = null
          }
          // Formula path also valid for numeric/currency advanced cells.
          if (isFormulaText(v) && (cellType === 'number' || cellType === 'currency')) {
            const result = evaluateCell(v, formulaCtx)
            const existing = (item.formulas ?? {}) as Record<string, string>
            const nextFormulas = { ...existing, [col.id]: v }
            onUpdate({
              [col.id]: result.ok ? result.value : null,
              formulas: nextFormulas,
            } as Partial<QuoteLineItem>)
            return
          }
          onUpdate({ [col.id]: coerced } as Partial<QuoteLineItem>)
        }
        const align: 'left' | 'right' | 'center' =
          col.align === 'left' || col.align === 'right' || col.align === 'center'
            ? col.align
            : cellType === 'number' || cellType === 'currency'
            ? 'right'
            : 'left'
        return (
          <EditableCell
            key={col.id}
            field={col.id}
            value={display}
            optimisticValue={getOptimisticValue?.(item.id, col.id)}
            errorState={getCellErrorState?.(item.id, col.id) || 'none'}
            isActive={isCellActive(colIdx)}
            isEditing={isEditing && isCellActive(colIdx)}
            editValue={editValueForCell}
            onStartEdit={() => onSelect(colIdx, { startEditing: true })}
            onSetEditValue={onSetEditValue}
            onCommit={onCommitAdvanced}
            onKeyDown={onCellKeyDown}
            type={cellType}
            align={align}
            editable={isEditable}
            formula={formulasMap[col.id] ?? null}
            formulaContext={formulaCtx}
            className="bg-rose-50/20 dark:bg-rose-950/10 text-slate-700 dark:text-slate-300"
          />
        )
      })}

      {/* Custom columns (col 17+): cascade line.custom_values[key] -> default_value */}
      {(customColumns ?? []).map((cc, i) => {
        const colIdx = FIXED_COLUMN_COUNT + i
        const customMap = (item.custom_values ?? {}) as Record<string, unknown>
        const lineVal = customMap[cc.key]
        const fallback = cc.default_value
        const display: unknown =
          lineVal !== undefined && lineVal !== null && lineVal !== ''
            ? lineVal
            : fallback
        const editValueForCell = isCellActive(colIdx)
          ? editValue
          : (display === undefined || display === null ? '' : String(display))
        const cellType: 'text' | 'number' | 'currency' = cc.type ?? 'text'
        const onCommitCustom = (v: string) => {
          let coerced: unknown = v
          if (cellType === 'number' || cellType === 'currency') {
            coerced = v === '' ? null : Number.parseFloat(v)
            if (Number.isNaN(coerced)) coerced = null
          }
          onUpdate({
            custom_values: { ...customMap, [cc.key]: coerced },
          } as Partial<QuoteLineItem>)
        }
        return (
          <EditableCell
            key={cc.key}
            field={cc.key}
            value={display}
            optimisticValue={getOptimisticValue?.(item.id, cc.key)}
            errorState={getCellErrorState?.(item.id, cc.key) || 'none'}
            isActive={isCellActive(colIdx)}
            isEditing={isEditing && isCellActive(colIdx)}
            editValue={editValueForCell}
            onStartEdit={() => onSelect(colIdx, { startEditing: true })}
            onSetEditValue={onSetEditValue}
            onCommit={onCommitCustom}
            onKeyDown={onCellKeyDown}
            type={cellType}
            align={cellType === 'number' || cellType === 'currency' ? 'right' : 'left'}
            editable={isEditable}
            className={cn(
              'bg-rose-50/30 dark:bg-rose-950/20 text-rose-700 dark:text-rose-300',
              // visually distinguish lines that are using the cascade default
              // (no per-line override) so estimators see at a glance which
              // values are user-typed vs Setup-derived.
              (lineVal === undefined || lineVal === null || lineVal === '') &&
                fallback !== undefined && fallback !== null &&
                'italic text-rose-500 dark:text-rose-400',
            )}
          />
        )
      })}
    </div>
  )
}
